import type { CategoryContent } from './row.js';

export const ORGANIC_CHEMISTRY: CategoryContent = {
  bscba: [
    [
      'Which reaction type is characterized by the nucleophile attacking a carbon bearing a good leaving group in a single step, with inversion of stereochemistry?',
      [
        'SN2 substitution',
        'Unimolecular elimination (E1)',
        'Electrophilic aromatic substitution (EAS)',
        'Nucleophilic acyl substitution',
      ],
      0,
      'SN2 involves a backside attack in one step, inverting stereochemistry. E1 is unimolecular and gives racemization; EAS and acyl substitution are mechanistically distinct.',
    ],
    [
      'What is the primary functional group present in a carboxylic acid?',
      [
        'Carboxyl group (-COOH)',
        'An aldehyde with an extra oxygen',
        'An ester on a primary alcohol',
        'A ketone with a nearby hydroxyl group',
      ],
      0,
      'The carboxyl group -COOH consists of C=O and O-H. Aldehydes, esters, and ketones are different functional groups with distinct chemistry.',
    ],
    [
      'In an E2 elimination reaction, the hydrogen and leaving group adopt what spatial relationship?',
      [
        'Antiperiplanar arrangement',
        'Gauche arrangement',
        'Eclipsed arrangement',
        'No specific spatial requirement',
      ],
      0,
      'E2 requires antiperiplanar geometry for simultaneous C-H and C-LG bond cleavage. Gauche and eclipsed are not requirements; E2 is stereospecific.',
    ],
    [
      'Which reagent is commonly used to oxidize a primary alcohol to an aldehyde?',
      [
        'Pyridinium chlorochromate (PCC)',
        'Potassium permanganate (KMnO4)',
        'Lithium aluminum hydride (LiAlH4)',
        'Sodium borohydride (NaBH4)',
      ],
      0,
      'PCC selectively oxidizes primary alcohols to aldehydes under mild conditions. KMnO4 over-oxidizes; LiAlH4 and NaBH4 are reducing agents.',
    ],
    [
      'What does the term "regioselectivity" describe in organic reactions?',
      [
        'Preference for one positional isomer',
        'Preference for one stereoisomer over another',
        'The speed at which a reaction reaches equilibrium',
        'The amount of side product formed',
      ],
      0,
      'Regioselectivity refers to which region of a molecule reacts. Stereoselectivity involves stereoisomers; reaction rate and yield are separate concepts.',
    ],
    [
      'Which statement correctly describes a Grignard reagent (RMgX)?',
      [
        'An organometallic nucleophile',
        'An alkyl halide used in elimination reactions',
        'A catalytic species for hydrogenation',
        'An oxidizing agent for primary alcohols',
      ],
      0,
      'Grignard reagents are organometallic nucleophiles valuable for C-C bond formation with carbonyls. Alkyl halides are electrophiles; Grignards are not catalysts or oxidants.',
    ],
    [
      'What is the relationship between (R)-glyceraldehyde and (S)-glyceraldehyde?',
      [
        'They are enantiomers',
        'They are diastereomers',
        'They are constitutional isomers',
        'They are the same molecule',
      ],
      0,
      'Enantiomers are non-superimposable mirror images differing at one chiral center. Diastereomers differ at some but not all stereocenters; constitutional isomers have different connectivity.',
    ],
    [
      'In nucleophilic acyl substitution, which intermediate is formed after the nucleophile attacks the carbonyl?',
      [
        'A tetrahedral intermediate',
        'A carbocation',
        'An enolate anion',
        'An epoxide',
      ],
      0,
      'Nucleophilic attack on a flat sp² carbonyl carbon forms a tetrahedral intermediate. Carbocations, enolates, and epoxides are distinct species.',
    ],
    [
      'Which functional group is most easily oxidized under mild conditions?',
      [
        'A primary alcohol',
        'An ether',
        'An amide',
        'An alkene',
      ],
      0,
      'Primary alcohols are readily oxidized by chromium(VI) and other mild oxidants to aldehydes. Ethers, amides, and alkenes resist oxidation under typical conditions.',
    ],
    [
      'What type of molecule results from a Diels-Alder reaction?',
      [
        'A cyclohexene',
        'A cyclobutene (four-membered ring)',
        'A bicyclic compound',
        'An open-chain diene',
      ],
      0,
      'The [4+2] Diels-Alder cycloaddition forms a six-membered ring. Cyclobutene results from [2+2]; bicyclic products require multiple reactions; open-chain products aren\'t cycloadducts.',
    ],
    [
      'In SN1 mechanism, what is the rate-determining step?',
      [
        'Carbocation formation (heterolytic C-LG bond cleavage)',
        'Nucleophile attack on the C-LG bond',
        'Rearrangement of the alkyl chain',
        'Departure of the leaving group after nucleophile binding',
      ],
      0,
      'The slow step in SN1 is formation of the carbocation intermediate. Nucleophile attack is fast; rearrangement is optional; LG departure precedes nucleophile binding.',
    ],
    [
      'Which compound is a primary amine?',
      [
        'CH3-CH2-NH2',
        'CH3-CH2-NH-CH3',
        '(CH3)3N',
        'C6H5-NH2 with a benzyl substituent on N',
      ],
      0,
      'A primary amine has one alkyl/aryl group on nitrogen and two hydrogens. Secondary amines have two groups; tertiary amines have three; N-substituted anilines are also secondary.',
    ],
    [
      'What is the stereochemical outcome of hydrogenation of an alkene using H2 and a catalyst?',
      [
        'Syn addition (H atoms add to the same face)',
        'Anti addition (H atoms add to opposite faces)',
        'A mixture of syn and anti products',
        'Inversion of the double bond geometry',
      ],
      0,
      'Catalytic hydrogenation delivers both H atoms to the same face of the alkene (syn). Anti addition occurs with halogens; mixtures and geometric inversion don\'t occur.',
    ],
    [
      'Which statement describes the Markovnikov\'s rule?',
      [
        'The nucleophile adds to the carbon with more hydrogens',
        'In elimination, the more substituted alkene is always the major product',
        'In substitution, primary carbons are always attacked before secondary',
        'Racemic mixtures always form in polar solvents',
      ],
      0,
      'Markovnikov\'s rule predicts regioselectivity: H adds to the carbon with more H atoms (less substituted carbon). It does not dictate elimination selectivity or substitution patterns.',
    ],
    [
      'What functional group is formed when a ketone is reduced by sodium borohydride?',
      [
        'A secondary alcohol',
        'A primary alcohol',
        'An alkane',
        'An aldehyde',
      ],
      0,
      'NaBH4 reduces ketones (and aldehydes) to alcohols; ketones yield secondary alcohols. Primary alcohols come from aldehydes; alkanes require stronger reducing agents.',
    ],
  ],
  msc: [
    [
      'In the SN1 mechanism of a secondary alkyl halide, which factor most significantly stabilizes the carbocation intermediate?',
      [
        'Hyperconjugation from α-alkyl groups',
        'Resonance stabilization from an adjacent aromatic ring',
        'Solvation by polar protic solvent molecules',
        'Steric relief upon leaving group departure',
      ],
      0,
      'Hyperconjugation and inductive donation from neighboring carbons stabilize secondary carbocations. Aromatic resonance only applies to benzyl; solvation aids rate but doesn\'t stabilize intermediates.',
    ],
    [
      'Why does 1,2-dimethyloxirane (propylene oxide) undergo regioselective ring opening toward the less substituted carbon when attacked by a nucleophile in basic conditions?',
      [
        'SN2-type backside attack at the less hindered carbon',
        'Direct nucleophile approach to the more substituted center due to electrostatic polarity',
        'Carbocation formation preferentially at the more substituted site',
        'Steric blocking of the more substituted carbon by methyl groups',
      ],
      0,
      'Epoxide SN2 ring opening favors the least hindered carbon via backside attack. Unlike carbocation formation, epoxide opening under basic/nucleophilic conditions is bimolecular; the more substituted carbon is sterically blocked.',
    ],
    [
      'In E1cb (conjugate base) elimination, what intermediate is key before C-H bond cleavage?',
      [
        'An enolate or carbanion intermediate',
        'A carbocation intermediate',
        'A cyclic three-membered-ring intermediate',
        'A π-complex with the base catalyst',
      ],
      0,
      'E1cb deprotonation precedes LG loss, forming an enolate/carbanion. This orders as E1-like (E) but carbanion-dependent (1cb), distinct from carbocation E1 or syn E2.',
    ],
    [
      'Which combination of factors makes a compound a poor Diels-Alder dienophile?',
      [
        'An electron-donating group with low steric demand',
        'Electron-withdrawing groups (EWGs) on the double bond',
        'Steric hindrance from bulky neighboring groups',
        'High symmetry of the dienophile',
      ],
      0,
      'Diels-Alder dienophiles must be electron-deficient (EWGs accelerate the reaction). Electron donors deactivate; steric demand hinders approach but isn\'t inherently disqualifying; symmetry is orthogonal.',
    ],
    [
      'In the Wittig reaction, what intermediate is formed after the ylide attacks the aldehyde carbonyl?',
      [
        'A betaine (1,2-dipolar zwitterion) that closes to an oxirane',
        'An ylide-aldehyde adduct that immediately extrudes phosphine oxide',
        'A carbocation-phosphonium intermediate',
        'An enol that is stabilized by the phosphorus atom',
      ],
      0,
      'Wittig ylide attack yields a betaine, which closes to a three-membered oxirane; decomposition gives alkene and phosphine oxide. Other options misrepresent the sequence.',
    ],
    [
      'Why does the E2 elimination of a primary alkyl halide rarely occur despite favorable kinetics, while secondary and tertiary alkyl halides readily undergo E2?',
      [
        'Primary carbocations are too unstable; SN2 dominates instead',
        'Primary C-H bonds are too strong to break',
        'Base-catalyzed isomerization blocks elimination',
        'Solvent viscosity preferentially inhibits E2 at primary sites',
      ],
      0,
      'Primary alkyl halides favor SN2 over E2 because the primary carbocation is drastically unstable; bimolecular substitution outcompetes unimolecular elimination. C-H strength is not the barrier; isomerization and solvent effects are not primary factors.',
    ],
    [
      'In a retrosynthetic analysis, what does the "umpoled carbonyl" concept address?',
      [
        'Reversing carbonyl polarity from electrophilic to nucleophilic',
        'Converting a ketone to an enol for selective substitution',
        'Introducing additional stereocenters via aldol condensation',
        'Activating a carbonyl group via protection as a ketal',
      ],
      0,
      'Umpolung (Ger. "polarity reversal") converts a normally electrophilic carbonyl carbon into a nucleophile (e.g., via dithianes). Enolization, aldol, and ketal protection are different transformations.',
    ],
    [
      'What is the stereochemical outcome of a [2,3]-sigmatropic rearrangement of an allylic sulfoxide in the Mislow rearrangement?',
      [
        'Suprafacial, with inversion at sulfur and allylic carbon',
        'Antarafacial rearrangement, generating a new alkene geometry',
        'No net change in stereochemistry at either center',
        'Exclusive formation of the (R,R)-diastereomer regardless of starting material',
      ],
      0,
      'The [2,3]-sigmatropic rearrangement is suprafacial; both sulfur inversion and allylic carbon inversion occur, yielding a predictable stereochemical outcome. Antarafacial mechanism, no change, and diastereomer exclusivity are incorrect.',
    ],
    [
      'In the Heck reaction catalyzed by Pd(0), what is the first step after oxidative addition of the organic halide?',
      [
        'Migratory insertion of an alkene into the Pd-C bond',
        'Coordination of the alkene to the Pd center',
        'Reductive elimination to form the coupled product',
        'β-hydride elimination from the alkyl palladium',
      ],
      0,
      'After oxidative addition, the alkene coordinates to Pd and inserts into the Pd-alkyl bond (migratory insertion). Reductive elimination is final; β-hydride elimination follows insertion.',
    ],
    [
      'What is the fundamental difference between a diastereomer and an enantiomer in the context of prochirality?',
      [
        'Enantiotopic groups become diastereomeric only with a chiral reagent',
        'Both are invisible to achiral reagents; both require chiral comparison to detect',
        'Enantiotopic groups require a mirror plane; diastereotopic groups do not',
        'Enantiomers are only distinguishable by chiral solvents; diastereomers always have different NMR signals',
      ],
      0,
      'Enantiotopic groups become diastereomeric when treated with a chiral reagent. Diastereotopic groups are intrinsically different (different NMR in achiral solvents). Both concepts clarify symmetry analysis.',
    ],
    [
      'In olefin metathesis catalyzed by Grubbs\' catalyst, what is the metal-carbene species that initiates the reaction?',
      [
        'A ruthenium alkylidene (Ru=CH-R) complex that [2+2]-adds to the olefin',
        'A ruthenium carbocation coordinated to a cationic phosphine',
        'A metathesis complex that simultaneously binds two alkenes',
        'A tungsten alkylidene that is less selective than Ru-based catalysts',
      ],
      0,
      'Grubbs\' catalyst is a Ru(II) alkylidene that reacts with alkenes via [2+2] cycloaddition of the Ru=C π-bond. The carbocation, dual-alkene binding, and tungsten comparison are inaccurate.',
    ],
    [
      'Why is the Cope rearrangement a [3,3]-sigmatropic shift rather than a [1,5]-shift?',
      [
        '[3,3] is suprafacial-allowed; [1,5] is antarafacial-forbidden',
        '[1,5] shifts require a chiral center that directs selectivity',
        'The Cope rearrangement forms a six-membered transition state, which is geometrically incompatible with [1,5]',
        'Only [3,3] shifts are possible in 1,5-dienes due to molecular symmetry',
      ],
      0,
      'Cope rearrangement involves 6π electrons (4n+2, n=1), making suprafacial [3,3]-shift thermally allowed. A [1,5]-shift would be antarafacial with 6π electrons, which is forbidden. Chirality, transition state size, and symmetry arguments are not primary.',
    ],
    [
      'In protecting group strategy, what property makes benzyl ethers suitable for protecting alcohols in multi-step synthesis?',
      [
        'Cleaved by hydrogenolysis (H2/Pd), stable to acid/base',
        'Orthogonal to acetyl esters via selective saponification',
        'Stable under both acidic and basic aqueous conditions, but cleaved only by strong oxidants',
        'Easily installed by Williamson ether synthesis and cleaved by excess Grignard reagent',
      ],
      0,
      'Benzyl ethers are cleaved reductively (H2/Pd-C) and stable to acid/base, enabling independent manipulation of other functional groups. Acetyl esters, strong oxidants, and Grignard cleavage are not characteristic of benzyl protection.',
    ],
    [
      'What does the term "diastereofacial selectivity" (or "diastereofacial discrimination") mean in asymmetric synthesis?',
      [
        'Preferential attack on one face of a prochiral group',
        'Selective formation of one geometric isomer of an alkene',
        'Preferential reaction at one stereocentered position in a meso compound',
        'The tendency of enantiomers to crystallize separately',
      ],
      0,
      'Diastereofacial selectivity describes how a chiral catalyst/reagent can distinguish between the two faces of a prochiral group, forming predominantly one diastereomer. Geometric isomerism, meso reactivity, and crystallization are separate concepts.',
    ],
    [
      'In natural product synthesis, why is the asymmetric Sharpless dihydroxylation (AD) widely used despite requiring stoichiometric chiral ligand?',
      [
        'High enantioselectivity with catalytic osmium turnover',
        'It is more economical than chiral auxiliary methods and forms only one stereoisomer',
        'It operates as a true catalytic process with substoichiometric osmium',
        'It requires no protecting groups and is compatible with all functional groups',
      ],
      0,
      'Sharpless AD is valuable for its high ee and direct syn-diol formation; osmium is recycled (catalytic), though the AD reagent and ligand stoichiometry must be prepared. Stoichiometric ligand, selectivity, and functional group compatibility are real; catalytic claims must be precise.',
    ],
  ],
  phd: [
    [
      'In the Eschenmoser-Claisen rearrangement, what is the intermediate that triggers the thermal [3,3]-sigmatropic shift?',
      [
        'Formation of an allylic ketene aminal (imidate)',
        'A carbocation generated from acid-catalyzed activation of the hydroxyl group',
        'An enolate formed by deprotonation of a β-hydrogen',
        'A concerted intermediate in which the allylic C-O bond breaks synchronously with C-C formation',
      ],
      0,
      'Eschenmoser-Claisen uses a ketene aminal intermediate (or equivalent imidate/sulfur ylide complex) that undergoes [3,3]-rearrangement. Carbocation, enolate, and direct concerted pathways misrepresent the mechanism.',
    ],
    [
      'What substrate class and solvent conditions favor efficient nucleophilic displacement in the Finkelstein reaction, given the reactivity of the metal halides involved?',
      [
        'Primary or secondary halides in aprotic solvents favor SN2',
        'Tertiary halides undergo E2 preferentially due to steric hindrance, and polar protic solvents suppress nucleophilicity',
        'The reaction is purely thermodynamic, determined by the stability of the products and metal halide crystallization',
        'Alkyl fluorides are most reactive because C-F bonds are polarized toward the nucleophile',
      ],
      0,
      'Finkelstein succeeds with primary/secondary halides in aprotic solvents (enhanced anion nucleophilicity, suppressed carbocations). Tertiary substrates eliminate; protic solvents harm nucleophilicity.',
    ],
    [
      'In a pericyclic [4+2] Diels-Alder reaction, what does the orbital symmetry analysis predict about the possibility of an allowed versus forbidden stereoelectronic pathway?',
      [
        'Suprafacial [4+2] is allowed; antarafacial [4+2] is forbidden',
        'Both suprafacial and antarafacial [4+2] pathways are equally allowed due to the symmetry of conjugated dienes',
        'Antarafacial [4+2] is preferred when electron-withdrawing groups are present on the dienophile',
        'The Diels-Alder is inherently a [2+4] reaction (dienophile first-named), changing the allowed/forbidden assignment',
      ],
      0,
      '[4+2] with 6π electrons (4n+2) is suprafacial-allowed. Antarafacial would require opposite-face orbital overlap, geometrically infeasible for dienes/dienophiles.',
    ],
    [
      'In transition metal catalysis, what is the mechanism by which oxidative addition of an organic halide to Pd(0) differs in regioselectivity and mechanism from an SN2 displacement?',
      [
        'Oxidative addition forms Pd(II)-C and Pd(II)-X at the metal',
        'Both proceed through the same carbocation intermediate, but the metal stabilizes a different stereoisomer',
        'Oxidative addition prefers secondary halides; SN2 prefers primary halides for the same substrate',
        'The regioselectivity is determined by the ionic strength of the solvent and the size of the halide',
      ],
      0,
      'Oxidative addition inserts Pd(0) into the C-X bond, forming Pd(II)-C and Pd(II)-X. SN2 is nucleophilic attack on carbon with inversion. Mechanisms and substrates differ fundamentally.',
    ],
    [
      'In the Cope rearrangement of a substituted 1,5-hexadiene with electron-donating and electron-withdrawing groups, how do substituent electronic effects and steric hindrance influence the transition state?',
      [
        'EWG/EDG placement lowers ΔG‡; steric congestion disfavors it',
        'EDGs uniformly accelerate the reaction regardless of position; steric effects are negligible',
        'The rearrangement is under orbital control, so electronic effects are irrelevant',
        'Substituent effects are additive and increase ΔG‡ linearly with the number of electron-donating groups',
      ],
      0,
      'Cope rearrangement follows pericyclic (orbital) control rules; electronic effects (EWGs accelerate) and steric bulk at the six-membered transition state both influence rate. EDGs don\'t uniformly accelerate; orbital control doesn\'t exclude electronic effects; additivity is often nonlinear.',
    ],
    [
      'In catalytic enantioselective C-H functionalization, what strategic advantage does asymmetric catalysis via a chiral directing group offer over stoichiometric chiral auxiliaries?',
      [
        'Catalytic turnover needs less chiral ligand; it stays chelation-controlled',
        'Directing groups are always more selective than stoichiometric auxiliaries because they are permanently attached',
        'C-H functionalization requires stoichiometric chiral auxiliaries; catalytic methods cannot achieve enantioselectivity',
        'The stereoselectivity is purely determined by steric bulk of the catalyst, independent of the substrate',
      ],
      0,
      'Catalytic C-H functionalization with directing groups uses substoichiometric catalyst and chiral ligands. Stoichiometric auxiliaries require 1:1 ratio; site-selectivity is chelation-controlled.',
    ],
    [
      'In the retrosynthetic planning of a target with multiple stereocenters, what is the strategic advantage of an "asymmetric synthetic equivalent" (or "asymmetric Umpolung")?',
      [
        'It sets stereochemistry early via one enantioselective step',
        'It reduces the number of total steps by eliminating the need for any chiral reagents',
        'It guarantees all downstream transformations will occur with the same relative stereochemistry',
        'It is only applicable to natural products containing a single stereocentre',
      ],
      0,
      'Asymmetric synthetic equivalents set stereochemistry early via one enantioselective reaction, reducing downstream stereoselective transformations needed.',
    ],
    [
      'What is the origin of the regioselectivity when the carbopalladium intermediate in an intramolecular Heck reaction can cyclize via either 5-exo-trig or 6-endo-trig pathways?',
      [
        '5-exo-trig is kinetically favored by Baldwin\'s rules',
        'The 6-endo-trig is always faster due to the geometry of the Pd-alkene π-complex',
        'The selectivity is purely thermodynamic and depends on the relative stability of the two possible carbocations',
        'Endo-trig and exo-trig rules apply only to non-catalytic reactions',
      ],
      0,
      'Baldwin\'s rules favor 5-exo-trig (faster kinetics) over 6-endo-trig (slower). Pd catalyst stabilizes the exo pathway. Kinetic ring-closure selectivity overrides thermodynamics.',
    ],
    [
      'In polymer chemistry, what is the key mechanistic distinction between chain-growth polymerization via free radicals versus living anionic polymerization in terms of chain termination and molecular weight distribution?',
      [
        'Free-radical termination is random (broad Mw); anionic has none',
        'Living anionic polymerization is faster but suffers from broader molecular weight distribution',
        'Free radicals give perfectly uniform chain length; anionic polymerization gives random lengths',
        'Both mechanisms give identical polydispersity indices if the same monomer is used',
      ],
      0,
      'Free-radical polymerization terminates randomly (broad Mw). Living anionic has no termination (dormant carbanion stable), yielding narrow Mw and controlled growth.',
    ],
    [
      'In natural product synthesis, what is the purpose of a "strategic bond disconnection" in retrosynthetic analysis, and how does it differ from a "tactical disconnection"?',
      [
        'Strategic cuts simplify the target; tactical cuts handle local details',
        'Strategic disconnections are always in the middle of the molecule; tactical disconnections are always at the ends',
        'Both refer to the same concept, but "strategic" is used for complex targets and "tactical" for simple ones',
        'Strategic disconnections must be made first, in order, with no flexibility in planning order',
      ],
      0,
      'Strategic disconnections reduce the target to key building blocks (simplifying the synthetic tree); tactical disconnections address functional-group masks and regiocontrol. Position in the molecule and planning flexibility vary; the concepts are distinct in purpose and scope.',
    ],
    [
      'In the Cram chelation model for asymmetric induction, why does chelation of the nucleophile (or Grignard) by a β-amino alcohol or hydroxyl group override Felkin-Anh steric control?',
      [
        'Chelation fixes the nucleophile\'s approach face, overriding sterics',
        'Chelation weakens the C=O bond, making the carbon more electrophilic and less dependent on steric orientation',
        'Felkin-Anh control is only valid for non-chelating Grignard reagents',
        'Chelation and Felkin-Anh are equally important and no single rule dominates the selectivity',
      ],
      0,
      'Cram chelation creates a rigid, directable geometry favoring one face; this stereochemical bias outweighs Felkin-Anh steric arguments. Bond activation and Grignard type are not primary mechanisms; the models are sometimes competing, with chelation\'s geometric constraint winning.',
    ],
    [
      'In enantioselective epoxidation (e.g., Jacobsen\'s catalyst or Katsuki\'s method), what is the stereochemical role of the chiral ligand in determining the enantioselectivity of attacking one face of a prochiral olefin?',
      [
        'The chiral pocket creates diastereomeric TS; one is favored',
        'The catalyst does not distinguish between olefin faces; enantioselectivity arises solely from the inherent reactivity of the olefin',
        'The bulky ligand physically blocks one face of the olefin, preventing approach from that direction',
        'The ligand induces a temporary chiral center on the olefin itself, which directs the subsequent reaction',
      ],
      0,
      'Asymmetric epoxidation uses a chiral metal complex creating diastereomeric TS with one approach significantly favored (prochiral face discrimination).',
    ],
    [
      'In the Ireland-Claisen rearrangement, what is the advantage of using the silyl ketene acetal over the free enolate in terms of reaction mechanism and selectivity?',
      [
        'The silyl ketene acetal gives a cleaner [3,3]-rearrangement',
        'The silyl group acts as a leaving group, converting the rearrangement into an SN2 substitution',
        'Free enolates are more selective but require stoichiometric base; silyl acetals eliminate that need',
        'The silyl ketene acetal and free enolate give identical results, differing only in the base required',
      ],
      0,
      'Silyl ketene acetals are pre-organized, bench-stable, and undergo [3,3]-rearrangement cleanly with minimal side reactions; the Si-O bond aids transition state stabilization. Si is not a leaving group; base stoichiometry and reactivity differ significantly; results are not identical.',
    ],
    [
      'In cascade reactions (or domino reactions), what defines the "auto-catalytic" or "auto-propagating" nature of certain multi-component cyclizations?',
      [
        'Early-step products serve as reactant or catalyst for later steps',
        'All cascade reactions are inherently auto-catalytic if performed in one pot',
        'The reaction becomes faster if the product is continuously removed from the reaction mixture',
        'Auto-catalysis is only possible in multi-step syntheses involving at least three different reagents',
      ],
      0,
      'Cascade reactions self-propagate when an early product reacts as a partner or catalyst in the next step (true auto-catalysis). Not all one-pot reactions are cascades; product removal doesn\'t define auto-catalysis; the number of reagents is irrelevant to the catalytic mechanism.',
    ],
    [
      'In click chemistry, particularly copper(I)-catalyzed azide-alkyne cycloaddition (CuAAC), why is the Cu(I) catalyst essential for controlling regioselectivity and reaction rate?',
      [
        'Cu(I) activates the alkyne for concerted [3+2] to the 1,4-triazole',
        'Cu(I) acts as a simple counter-ion with no electronic effect on the alkyne',
        'The reaction is equally fast without copper, but copper is added for safety reasons',
        'Cu(I) catalyzes the rearrangement of the initial 1,5-triazole isomer to the thermodynamic 1,4-product',
      ],
      0,
      'Cu(I) complexes the alkyne, lowering its HOMO and enabling concerted [3+2] cycloaddition. The 1,4-regioisomer forms directly (kinetic product), not via rearrangement.',
    ],
  ],
  professor: [
    [
      'In transition-metal-catalyzed C-H functionalization, explain the selectivity governing ortho, meta, and para-substitution in arene C-H activation when the directing group is not a strongly coordinating atom.',
      [
        'Electronic/steric properties and weak chelation determine positional selectivity',
        'Para-selectivity is universally preferred due to resonance stabilization of the arene π-system',
        'Meta-selectivity is impossible in aromatic C-H activation unless a deactivating group is present',
        'Ortho, meta, and para-substitution occur with equal probability unless a strongly chelating directing group is introduced',
      ],
      0,
      'C-H activation selectivity arises from chelation geometry, electronic factors, and steric bulk. Weak hydrogen bonding and secondary chelation modulate ortho/meta/para selectivity.',
    ],
    [
      'In the context of photochemical [2+2] cycloaddition versus thermal Diels-Alder cycloaddition, how do the Woodward-Hoffmann rules explain the apparent "reversal" of stereochemical allowed/forbidden pathways?',
      [
        'The excited state changes orbital symmetry, reversing [2+2] rules',
        'Photochemistry simply accelerates thermal pathways without changing the selection rules',
        'Diels-Alder reactions are photochemical while [2+2] cycloadditions are thermal, never the reverse',
        'The "reversal" is an artifact of solvent effects, not orbital symmetry',
      ],
      0,
      '[2+2] photochemical is suprafacial-allowed because photon promotion changes electronic configuration, inverting outcomes versus thermal. Orbital symmetry, not solvent, governs.',
    ],
    [
      'In enantioselective phase-transfer catalysis, what catalyst design feature (or catalyst structural motif) enables the chiral quaternary ammonium ion to achieve high enantiocontrol in reactions like asymmetric epoxidation or alkylation of enolates?',
      [
        'Bulky, rigid chiral groups on the ammonium ion shield one enolate face',
        'Simple alkyl chains on the ammonium center are sufficient if the solvent is carefully chosen',
        'The counter-ion (e.g., OH⁻ vs. Cl⁻) is the exclusive source of stereochemical control, independent of the ammonium structure',
        'Phase-transfer catalysis cannot achieve high enantioselectivity because the interface is inherently non-chiral',
      ],
      0,
      'Bulky, chiral quaternary ammonium catalysts (e.g., Maruoka\'s designs) organize ion-pair geometry and shield enolate faces, creating high ee. Alkyl chains alone lack enantiocontrol; counter-ion choice is secondary; modern PTC achieves very high ee via ligand design.',
    ],
    [
      'In natural product synthesis, the "Kishi rule" (or extended selectivity models) guides 1,3-asymmetric induction in acyclic polyol systems; explain the mechanistic basis for why an anti-Felkin trajectory is often observed in unexpected cases.',
      [
        'Hidden steric and electronic effects can favor a non-Felkin conformer',
        'Anti-Felkin outcomes are always due to chelation and never occur without a hydroxyl or amino group at the β-position',
        'The Kishi rule and Felkin-Anh model are contradictory, and one must always override the other',
        'Anti-Felkin selectivity arises purely from electronic activation of the aldehydic carbon by adjacent electron-withdrawing groups',
      ],
      0,
      'Kishi/anti-Felkin outcomes reflect conformational mobility and hydrogen bonding beyond simple steric arguments. Models are complementary, not contradictory.',
    ],
    [
      'In catalytic asymmetric C-C coupling via Pd-catalyzed cross-coupling, explain the origin of enantiofidelity (or lack thereof) when a Pd-alkyl intermediate bears a stereogenic carbon.',
      [
        'Fidelity depends on reductive elimination outpacing racemization',
        'Pd-alkyl intermediates always invert stereochemistry during coupling due to the nature of oxidative addition',
        'Enantiofidelity is impossible in cross-coupling; stereogenic alkyl groups are always lost or scrambled',
        'Only chiral ligands on the palladium can induce stereochemical fidelity; the substrate\'s stereochemistry is irrelevant',
      ],
      0,
      'Enantiofidelity depends on rates: fast reductive elimination outpaces β-hydride elimination and isomerization. Substrate stereochemistry and reactivity are essential.',
    ],
    [
      'In polymer synthesis, explain why controlled radical polymerization (ATRP or RAFT) enables access to narrow polydispersity and block copolymers, contrasting the mechanism with living anionic polymerization.',
      [
        'CRP keeps [radical] low via a dormant/active equilibrium',
        'ATRP and RAFT use anionic intermediates, making them essentially equivalent to living anionic polymerization',
        'Living anionic polymerization gives broader polydispersity than ATRP because it has no equilibrium control',
        'Controlled radical polymerization cannot make block copolymers because radicals do not live long enough for sequential addition',
      ],
      0,
      'CRP (ATRP/RAFT) maintains low [radical] via dormant/active equilibrium (controlled growth, narrow Mw). Anionic is directly growing (no dormant), incompatible with many functional groups.',
    ],
    [
      'In the total synthesis of a complex natural product, how do "protecting group-free" or "minimalist protecting group" strategies differ in philosophy and execution from traditional routes, and what are the key synthetic challenges?',
      [
        'It exploits inherent chemoselectivity instead of covalent protection',
        'Protecting-group-free synthesis is only possible for natural products with a single functional group',
        'Traditional routes are universally shorter than protecting-group-free routes',
        'Protecting-group-free synthesis uses the same reagents and transformations as traditional syntheses, differing only in naming convention',
      ],
      0,
      'Protecting-group-free synthesis relies on chemoselectivity and reactivity trends, reducing step count and waste. It is not limited to mono-functional targets; step count varies; the approach employs distinct reagent choices and mechanistic insights.',
    ],
    [
      'In asymmetric catalysis using chiral bisphosphine ligands (e.g., BINAP, DuPhos), explain the origin of the "bite angle" and how it influences reactivity and enantioselectivity in cross-coupling or hydrogenation reactions.',
      [
        'Bite angle (P-M-P) sets the coordination geometry and steric access',
        'Bite angle is determined solely by the length of the phosphorus-containing linker and does not affect reactivity',
        'All chiral bisphosphines have the same bite angle regardless of their structure',
        'Bite angle affects catalyst color and solubility but not reactivity or selectivity',
      ],
      0,
      'Bite angle (P-M-P) dictates metal coordination geometry and steric congestion, governing reactivity and enantioselectivity. Linker rigidity and backbone geometry control it.',
    ],
    [
      'In synthetic organic chemistry, what is the purpose and mechanistic advantage of using a "bulky nucleophile" or "hindered reducing agent" in a selective transformation where a smaller nucleophile would suffer from side reactions?',
      [
        'Bulky nucleophiles avoid hindered sites via steric exclusion',
        'Bulky nucleophiles are faster than small ones due to electronic activation',
        'Bulky nucleophiles cannot access sterically hindered sites, so they are used only when selectivity for less hindered sites is desired',
        'Size of the nucleophile is independent of selectivity; only the solvent polarity matters',
      ],
      0,
      'Bulky nucleophiles attack less hindered sites via steric exclusion (DIBAL-H, LTBA), enabling selective reductions. Selectivity arises from steric congestion around the nucleophile.',
    ],
    [
      'In the context of organocatalysis (imidazolidinone or thiourea catalysis), explain how hydrogen bonding between the catalyst and substrate creates a chiral microenvironment that leads to high enantioselectivity without a metal center.',
      [
        'H-bonding pre-organizes the substrate, distinguishing the two faces',
        'Hydrogen bonding in organocatalysis is incidental and does not contribute to selectivity',
        'Organocatalysts achieve selectivity purely through steric bulk, with no electrostatic or hydrogen-bonding component',
        'Hydrogen bonding and transition-state organization are only relevant in metal catalysis',
      ],
      0,
      'Organocatalytic H-bonding creates diastereomeric substrate-catalyst complexes and stabilizes one TS, enabling high ee. H-bonds and electrostatics are dominant.',
    ],
    [
      'In macrocyclic synthesis and ring-closing metathesis (RCM), explain how substrate structure (ring size, alkene geometry, and substitution pattern) influences the equilibrium and kinetics of RCM and the tendency toward unwanted oligomerization.',
      [
        'Thorpe-Ingold effects and intramolecular kinetics favor RCM',
        'All ring sizes close with equal efficiency in RCM',
        'RCM oligomerization is unavoidable and cannot be suppressed by substrate design',
        'Alkene geometry (E/Z) is irrelevant to RCM selectivity and product distribution',
      ],
      0,
      'RCM success depends on Thorpe-Ingold strain relief, TS geometry, and intramolecular kinetic advantage. Oligomerization risk scales with substrate concentration.',
    ],
    [
      'In total synthesis planning, what distinguishes a "key strategic transformation" from a routine step, and how does this guide retrosynthetic analysis?',
      [
        'It builds complexity (stereocenters, rings) in one step, guiding strategy',
        'Key transformations are always the final steps in a synthesis',
        'All transformations are equally important in route optimization',
        'Key transformations must be reactions well-established in the literature with >95% yield',
      ],
      0,
      'Key strategic transformations create complexity efficiently (stereocenters, rings); they dictate strategy even if not final steps or perfectly high-yield. Routine steps build on established knowledge.',
    ],
    [
      'In photochemical organic synthesis, explain the difference between a Type I and Type II photochemical mechanism in terms of the photoinitiated intermediates and selectivity outcomes.',
      [
        'Type I gives radicals; Type II transfers energy (singlet oxygen)',
        'Both types generate identical products and differ only in mechanism nomenclature',
        'Type I requires visible light; Type II requires UV light',
        'Type II is faster because singlet-oxygen is more reactive than radicals',
      ],
      0,
      'Type I photochemistry: excited state generates radicals (H-abstraction, fragmentation). Type II: excited state transfers energy (e.g., to O₂, forming singlet-oxygen). Different mechanisms, different selectivity.',
    ],
    [
      'In enzymatic catalysis applied to organic synthesis, what is the advantage of directed evolution of enzymes versus engineering a single active site based on structural predictions?',
      [
        'Directed evolution samples sequence space for non-intuitive variants',
        'Directed evolution is slower and less efficient than rational design',
        'Rational design always finds the optimal enzyme variant first',
        'Directed evolution cannot achieve enantioselectivity beyond the starting enzyme',
      ],
      0,
      'Directed evolution (mutagenesis + screening) samples sequence space empirically, often finding non-obvious solutions surpassing rational design. It enables substrate scope expansion and entirely new selectivity.',
    ],
    [
      'In green chemistry and atom economy, how does the concept of "atom economy" differ from "yield" in evaluating the environmental benignity of a synthesis route?',
      [
        'Atom economy is theoretical atom use; yield is actual output',
        'Atom economy and yield are identical terms',
        'Yield is more important than atom economy because high yield means minimal waste',
        'Atom economy is irrelevant if the reaction is carried out in a green solvent',
      ],
      0,
      'Atom economy (fraction of starting atoms in product, theory-based) and yield (actual vs. theoretical, experiment-based) are orthogonal metrics. Both matter for evaluating green chemistry.',
    ],
  ],
};
